import { ShareIcon, TokenIcons } from '../../../../assets/icons';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import { SubgraphActionMapper, Tokens } from '../../../../config/constants';
import useModalState from '../../../../context/modal/useModalState';
import {
    formatAmountWithSuffix,
    formatPercentages,
    formatUSD,
} from '../../../../helpers';
import { UserTrade } from '../../../../hooks/web3/trade/useUserTrades';
import AssetType from '../../../../components/AssetType';
import { parseEther } from 'ethers/lib/utils';
import { ShareModalType } from '../../../../modals/Share/ShareModal';
import InfoPopUp from '../../../../components/InfoPopUp';
import clsx from 'clsx';

type TradeRowProps = {
    trade: UserTrade;
};

const TradeRow: React.FC<TradeRowProps> = ({ trade }) => {
    const { openModal, setModalData } = useModalState();

    const handleShare = () => {
        openModal('shareClosedPositionModal');
        setModalData((prev) => ({
            ...prev,
            data: {
                position: trade,
                type: ShareModalType.ClosedPosition,
            },
        }));
    };

    return (
        <div className='row'>
            <Card>
                <div
                    className={`trades__type ${
                        trade.isShort ? 'short' : 'long'
                    } position-id-${trade.id}`}
                >
                    <AssetType
                        tradePair={trade.tradePair}
                        isShort={trade.isShort}
                        leverage={trade.leverage}
                    />
                </div>
                <div className='trades__action'>
                    <span className='column-label'>Action</span>
                    {SubgraphActionMapper[trade.action]}
                </div>
                <div className='trades__size'>
                    {TokenIcons[trade.tradePair?.asset.symbol ?? Tokens.FALLBACK]}
                    <span className='column-label'>Size</span>
                    {formatAmountWithSuffix(trade.volume || 0, {
                        minDecimals: 2,
                        currency: 'USD',
                        minValue: 1_000_000_000,
                    })}
                </div>
                <div className='trades__net-value'>
                    <span className='column-label'>Net Value</span>
                    {formatAmountWithSuffix(trade.netValue || 0, {
                        minDecimals: 2,
                        currency: 'USD',
                        minValue: 1_000_000_000,
                    })}
                </div>
                <div className='trades__collateral'>
                    <span className='column-label'>Collateral</span>
                    <span className='value'>
                        {formatAmountWithSuffix(trade.margin || 0, {
                            minDecimals: 2,
                            currency: 'USD',
                            minValue: 1_000_000_000,
                        })}
                    </span>
                </div>
                <div className='trades__entry-price'>
                    <span className='column-label'>Entry Price</span>
                    {formatUSD(trade.entryPrice || 0, {
                        minDecimals: 2,
                        maxDecimals: trade.tradePair?.decimalFormatting,
                    })}
                </div>
                <div className='trades__close-price'>
                    <span className='column-label'>Close Price</span>
                    <span>
                        {formatUSD(trade.closePrice || 0, {
                            minDecimals: 2,
                            maxDecimals: trade.tradePair?.decimalFormatting,
                        })}
                    </span>
                </div>
                <div className='trades__realized-pnl'>
                    <span className='column-label'>Realized PNL</span>
                    <div className='pnl-value-wrapper'>
                        <InfoPopUp
                            trigger={
                                <div
                                    className={`pnl-value ${
                                        parseEther(trade.pnl).gt(0)
                                            ? 'increased'
                                            : 'decreased'
                                    }`}
                                >
                                    <span>
                                        {formatAmountWithSuffix(trade.pnl || 0, {
                                            minValue: 100000,
                                            minDecimals: 2,
                                            currency: 'USD',
                                        })}
                                    </span>
                                    &nbsp;
                                    <span>
                                        ({parseEther(trade.pnlPercent).gt('0') ? '+' : ''}
                                        {formatPercentages(trade.pnlPercent)}%)
                                    </span>
                                </div>
                            }
                        >
                            <div className='mb'>
                                PnL:{' '}
                                <span
                                    className={clsx({
                                        increased: parseEther(trade.grossPnl || '0').gt(
                                            '0'
                                        ),
                                        decreased: parseEther(trade.grossPnl || '0').lt(
                                            '0'
                                        ),
                                    })}
                                >
                                    {
                                        <>
                                            {formatAmountWithSuffix(trade.grossPnl, {
                                                minValue: 10_000_000,
                                                minDecimals: 2,
                                                currency: 'USD',
                                            })}{' '}
                                            (
                                            {parseEther(trade.grossPnlPercent || '0').gt(
                                                '0'
                                            )
                                                ? '+'
                                                : ''}
                                            {formatAmountWithSuffix(
                                                trade.grossPnlPercent || 0,
                                                {
                                                    minValue: 10_000,
                                                    minDecimals: 2,
                                                }
                                            )}
                                            %)
                                        </>
                                    }
                                </span>
                            </div>
                            <div>
                                Fees:{' '}
                                <span
                                    className={clsx({
                                        increased: parseEther(trade.feePnl).gt('0'),
                                        decreased: parseEther(trade.feePnl).lt('0'),
                                    })}
                                >
                                    {
                                        <>
                                            {formatAmountWithSuffix(trade.feePnl, {
                                                minValue: 10_000_000,
                                                minDecimals: 2,
                                                currency: 'USD',
                                            })}{' '}
                                            (
                                            {parseEther(trade.feePnlPercent).gt('0')
                                                ? '+'
                                                : ''}
                                            {formatAmountWithSuffix(trade.feePnlPercent, {
                                                minValue: 10_000,
                                                minDecimals: 2,
                                            })}
                                            %)
                                        </>
                                    }
                                </span>
                            </div>
                        </InfoPopUp>
                        <Button variant='transparent' onClick={handleShare}>
                            <ShareIcon />
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default TradeRow;
